'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getAllWithSubspecialtiesByOrganization,
    deleteSpecialty,
} from '@/lib/api/specialties/specialties-api'
import { Specialty } from '@/lib/api/common/interfaces/specialty.interface'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdClose,
    MdMedicalServices,
    MdSubdirectoryArrowRight,
    MdExpandMore,
    MdExpandLess,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import ConfirmModal from '@/components/confirm-modal'

export default function SpecialtyListPage() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'registrations',
        'specialties'
    )
    const [allItems, setAllItems] = useState<Specialty[]>([])
    const [filteredItems, setFilteredItems] = useState<Specialty[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [specialtyToDelete, setSpecialtyToDelete] = useState<number | null>(
        null
    )
    const [deleting, setDeleting] = useState(false)
    const [expandedCategories, setExpandedCategories] = useState<Set<number>>(
        new Set()
    )

    useEffect(() => {
        if (user?.companyId) {
            loadSpecialties()
        }
    }, [user?.companyId])

    useEffect(() => {
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase()
            const filtered = allItems.filter(
                item =>
                    item.name.toLowerCase().includes(term) ||
                    item.parent?.name?.toLowerCase().includes(term)
            )
            setFilteredItems(filtered)
        } else {
            setFilteredItems(allItems)
        }
    }, [searchTerm, allItems])

    const loadSpecialties = async () => {
        if (!user?.companyId) return

        setLoading(true)
        try {
            const response = await getAllWithSubspecialtiesByOrganization(
                user.organizationId
            )
            if (response.success) {
                const result = Array.isArray(response.result)
                    ? response.result
                    : (response.result as any)?.data || []
                setAllItems(result)
                setFilteredItems(result)
            } else {
                toast.error(response.error || 'Erro ao carregar especialidades')
            }
        } catch {
            toast.error('Erro ao carregar especialidades')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (specialtyId: number) => {
        setSpecialtyToDelete(specialtyId)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!specialtyToDelete) return

        setDeleting(true)
        try {
            const response = await deleteSpecialty(specialtyToDelete)
            if (response.success) {
                toast.success('Especialidade excluída com sucesso')
                loadSpecialties()
                setShowDeleteModal(false)
                setSpecialtyToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir especialidade')
            }
        } catch {
            toast.error('Erro ao excluir especialidade')
        } finally {
            setDeleting(false)
        }
    }

    const toggleExpand = (id: number) => {
        setExpandedCategories(prev => {
            const next = new Set(prev)
            if (next.has(id)) {
                next.delete(id)
            } else {
                next.add(id)
            }
            return next
        })
    }

    const specialties = filteredItems.filter(item => !item.parentId)
    const subspecialties = filteredItems.filter(item => !!item.parentId)

    const totalSpecialties = allItems.filter(item => !item.parentId).length
    const totalSubspecialties = allItems.filter(item => !!item.parentId).length

    const getSubspecialtiesForParent = (parentId: number) => {
        return subspecialties.filter(sub => sub.parentId === parentId)
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">
                    Carregando especialidades...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdMedicalServices className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Especialidades
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {totalSpecialties}{' '}
                            {totalSpecialties === 1
                                ? 'especialidade'
                                : 'especialidades'}
                            {totalSubspecialties > 0 && (
                                <>
                                    {' '}
                                    e {totalSubspecialties}{' '}
                                    {totalSubspecialties === 1
                                        ? 'subespecialidade'
                                        : 'subespecialidades'}
                                </>
                            )}
                        </p>
                    </div>
                </div>
                {canCreate && (
                    <div className="flex items-center gap-2">
                        <button
                            className="btn btn-outline btn-secondary gap-2"
                            onClick={() =>
                                navigate(
                                    '/registrations/specialties/new?tipo=subespecialidade'
                                )
                            }
                        >
                            <MdSubdirectoryArrowRight className="text-xl" />
                            <span className="hidden sm:inline">
                                Subespecialidade
                            </span>
                        </button>
                        <button
                            className="btn btn-secondary gap-2"
                            onClick={() =>
                                navigate('/registrations/specialties/new')
                            }
                        >
                            <MdAdd className="text-xl" />
                            <span className="hidden sm:inline">
                                Especialidade
                            </span>
                        </button>
                    </div>
                )}
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                        <input
                            type="text"
                            placeholder="Buscar por nome..."
                            className="input input-bordered w-full pl-10"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                </div>
            </div>

            {filteredItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 bg-base-100 rounded-xl border border-base-300/50 p-8">
                    <div className="w-20 h-20 rounded-full bg-base-200 flex items-center justify-center">
                        <MdMedicalServices className="text-4xl text-base-content/30" />
                    </div>
                    <div className="text-center">
                        <h3 className="text-lg font-semibold text-base-content mb-1">
                            {searchTerm
                                ? 'Nenhuma especialidade encontrada'
                                : 'Nenhuma especialidade cadastrada'}
                        </h3>
                        <p className="text-sm text-base-content/60 mb-4">
                            {searchTerm
                                ? 'Tente ajustar sua busca'
                                : 'Comece adicionando as especialidades que sua empresa oferece'}
                        </p>
                        {!searchTerm && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/registrations/specialties/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Adicionar primeira especialidade
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {specialties.map(specialty => {
                        const subs = getSubspecialtiesForParent(specialty.id)
                        const isExpanded = expandedCategories.has(specialty.id)
                        const hasSubspecialties = subs.length > 0

                        return (
                            <div
                                key={specialty.id}
                                className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden"
                            >
                                <div className="flex items-center justify-between p-4">
                                    <div className="flex items-center gap-3">
                                        {hasSubspecialties ? (
                                            <button
                                                onClick={() =>
                                                    toggleExpand(specialty.id)
                                                }
                                                className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center hover:bg-secondary/20 transition-colors"
                                            >
                                                {isExpanded ? (
                                                    <MdExpandLess className="text-xl text-secondary" />
                                                ) : (
                                                    <MdExpandMore className="text-xl text-secondary" />
                                                )}
                                            </button>
                                        ) : (
                                            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                <MdMedicalServices className="text-xl text-secondary" />
                                            </div>
                                        )}
                                        <div>
                                            <h3 className="text-sm sm:text-base font-semibold text-base-content">
                                                {specialty.name}
                                            </h3>
                                            <p className="text-xs text-base-content/50">
                                                {hasSubspecialties && (
                                                    <span className="mr-2">
                                                        {subs.length}{' '}
                                                        {subs.length === 1
                                                            ? 'subespecialidade'
                                                            : 'subespecialidades'}
                                                    </span>
                                                )}
                                                Criada em{' '}
                                                {new Date(
                                                    specialty.createdAt
                                                ).toLocaleDateString('pt-BR')}
                                            </p>
                                        </div>
                                    </div>

                                    {(canUpdate || canDelete) && (
                                        <div className="flex items-center gap-2">
                                            {canUpdate && (
                                                <button
                                                    onClick={() =>
                                                        navigate(
                                                            `/registrations/specialties/${specialty.id}`
                                                        )
                                                    }
                                                    className="btn btn-xs sm:btn-sm btn-ghost"
                                                    title="Editar"
                                                >
                                                    <MdEdit className="text-base sm:text-lg" />
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() =>
                                                        handleDelete(
                                                            specialty.id
                                                        )
                                                    }
                                                    className="btn btn-xs sm:btn-sm btn-ghost text-error hover:bg-error/10"
                                                    title="Excluir"
                                                >
                                                    <MdDelete className="text-base sm:text-lg" />
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {hasSubspecialties && isExpanded && (
                                    <div className="border-t border-base-300/50 bg-base-200/20">
                                        {subs.map(sub => (
                                            <div
                                                key={sub.id}
                                                className="flex items-center justify-between px-4 py-3 border-b border-base-300/30 last:border-b-0"
                                            >
                                                <div className="flex items-center gap-3 pl-4">
                                                    <MdSubdirectoryArrowRight className="text-base-content/40" />
                                                    <div>
                                                        <h4 className="text-sm font-medium text-base-content">
                                                            {sub.name}
                                                        </h4>
                                                        <p className="text-xs text-base-content/50">
                                                            Criada em{' '}
                                                            {new Date(
                                                                sub.createdAt
                                                            ).toLocaleDateString(
                                                                'pt-BR'
                                                            )}
                                                        </p>
                                                    </div>
                                                </div>

                                                {(canUpdate || canDelete) && (
                                                    <div className="flex items-center gap-2">
                                                        {canUpdate && (
                                                            <button
                                                                onClick={() =>
                                                                    navigate(
                                                                        `/registrations/specialties/${sub.id}`
                                                                    )
                                                                }
                                                                className="btn btn-xs btn-ghost"
                                                                title="Editar"
                                                            >
                                                                <MdEdit className="text-base" />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={() =>
                                                                    handleDelete(
                                                                        sub.id
                                                                    )
                                                                }
                                                                className="btn btn-xs btn-ghost text-error hover:bg-error/10"
                                                                title="Excluir"
                                                            >
                                                                <MdDelete className="text-base" />
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}

                    {/* Subespecialidades orfas (sem especialidade pai visivel na busca) */}
                    {searchTerm &&
                        subspecialties
                            .filter(
                                sub =>
                                    !specialties.some(
                                        s => s.id === sub.parentId
                                    )
                            )
                            .map(sub => (
                                <div
                                    key={sub.id}
                                    className="group bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 overflow-hidden"
                                >
                                    <div className="flex items-center justify-between p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
                                                <MdSubdirectoryArrowRight className="text-xl text-secondary" />
                                            </div>
                                            <div>
                                                <h3 className="text-sm sm:text-base font-semibold text-base-content">
                                                    {sub.name}
                                                </h3>
                                                <p className="text-xs text-base-content/50">
                                                    Subespecialidade de{' '}
                                                    <span className="font-medium">
                                                        {sub.parent?.name ??
                                                            'N/A'}
                                                    </span>
                                                </p>
                                            </div>
                                        </div>

                                        {(canUpdate || canDelete) && (
                                            <div className="flex items-center gap-2">
                                                {canUpdate && (
                                                    <button
                                                        onClick={() =>
                                                            navigate(
                                                                `/registrations/specialties/${sub.id}`
                                                            )
                                                        }
                                                        className="btn btn-xs sm:btn-sm btn-ghost"
                                                        title="Editar"
                                                    >
                                                        <MdEdit className="text-base sm:text-lg" />
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button
                                                        onClick={() =>
                                                            handleDelete(sub.id)
                                                        }
                                                        className="btn btn-xs sm:btn-sm btn-ghost text-error hover:bg-error/10"
                                                        title="Excluir"
                                                    >
                                                        <MdDelete className="text-base sm:text-lg" />
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setSpecialtyToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir especialidade"
                message="Tem certeza de que deseja excluir esta especialidade? Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />
        </div>
    )
}
